import './table-columns.scss';

import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import TableDate from '@/components/Shared/TableData/TableDate';
import { ChosenBankStatus } from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES, type DictTypes } from '@/constants';
import { formatPhoneNumber } from '@/services/formatter';
import type { TDataSource } from '@/types/dataSource';
import type { TDict } from '@/types/dicts';

import { statusOptions } from '../../data/statusOptions.data';
import { RenderBankLogo, StatusTooltip } from '../../ui';
import type { ResponsesInterface } from './types';

interface IProps {
  statusesFilters: string[] | null;
  banksDict: TDict[DictTypes.Banks];
}

const getRowStyle = (
  record: TDataSource
): {
  style?: React.CSSProperties;
} => {
  if (record.status.isNotTakenOnline)
    return {
      style: {
        backgroundColor: '#dff1dfff'
      }
    };
  return {};
};

const autoCreditTableColumns: (props: IProps) => ColumnsType<TDataSource> = ({
  statusesFilters,
  banksDict
}) => [
  {
    title: 'Клиент',
    dataIndex: 'client',
    key: 'client',
    width: 240,
    onCell: getRowStyle,
    render: (_, record) => (
      <div className="auto-credit-table__cell">
        <div
          className={`auto-credit-table__avatar ${record.status.isNotTakenOnline ? 'auto-credit-table__avatar--online' : ''}`}
        >
          {record?.client?.fullName[0]?.toUpperCase()}
        </div>
        <div>
          <p className="auto-credit-table__ellipsis">
            {record?.client?.fullName}
          </p>
          <p className="auto-credit-table__cell-content">
            {formatPhoneNumber(record?.client?.telNumber)}
          </p>
        </div>
      </div>
    )
  },
  {
    title: 'Цена авто',
    dataIndex: 'loan',
    key: 'loan',
    width: 200,
    onCell: getRowStyle,
    render: (_, record) =>
      record.loan?.amount ? (
        <div className="auto-credit-table__cell-text">
          {`${record?.loan?.amount} ₸`}
          <p className="auto-credit-table__cell-content">
            {record?.loan?.auto || ''}
          </p>
        </div>
      ) : (
        <p className="auto-credit-table__no-info">Нет данных</p>
      )
  },
  {
    title: 'Тип заявки',
    dataIndex: 'orderType',
    key: 'orderType',
    width: 170,
    onCell: getRowStyle,
    render: (_, record) => {
      const autoType = record?.orderType?.autoType || '';
      const isOnline = record?.isOnlineRequest;
      return autoType ? (
        <div className="auto-credit-table__cell-text">
          <span>
            {autoType} •{' '}
            <span
              className={`auto-credit-table__request-type ${
                isOnline
                  ? 'auto-credit-table__request-type--online'
                  : 'auto-credit-table__request-type--offline'
              }`}
            >
              {isOnline ? 'Онлайн' : 'Офлайн'}
            </span>
          </span>
          <p className="auto-credit-table__cell-content">
            {record?.dealer?.name?.replace('(SAP)', '')}
          </p>
        </div>
      ) : (
        <p className="auto-credit-table__no-info">Нет данных</p>
      );
    }
  },
  {
    title: 'Создано',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 170,
    onCell: getRowStyle,
    render: (_, record) => (
      <div className="auto-credit-table__cell-text">
        {dayjs(record.created_at.date).format('DD.MM.YYYY, HH:mm')}
        {(!record.isOnlineRequest || !record.status.isNotTakenOnline) && (
          <p className="auto-credit-table__cell-content">
            {(record.isOnlineRequest
              ? record.takenUserFullname
              : record?.created_at?.author) || ''}
          </p>
        )}
      </div>
    )
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 192,
    filters: statusOptions,
    filteredValue: statusesFilters,
    onCell: getRowStyle,
    render: (_, record) => {
      const bank = banksDict?.getBy.name[record.status.selectedBank];
      const bankIconAndName =
        BANKS_ICONS_NAMES[
          record.status.selectedBank as keyof typeof BANKS_ICONS_NAMES
        ];

      const responses: ResponsesInterface[] = [];

      let hasBankResponses = false;

      if (
        record.status.approved.length > 0 ||
        record.status.leasingStatus.approved.length > 0
      ) {
        responses.push({
          banks: record.status.approved,
          leasings: record.status.leasingStatus.approved,
          color: '#0CAF60',
          type: 'approved',
          tooltipTitle: 'Одобрено предложение'
        });
        hasBankResponses ||= record.status.approved.length > 0;
      }

      const activeExpectationsBanks = record.status.expectations.filter(
        (item) => banksDict?.getBy.name[item.bank]?.is_active
      );
      if (
        activeExpectationsBanks.length > 0 ||
        record.status.leasingStatus.expectations.length > 0
      ) {
        responses.push({
          banks: activeExpectationsBanks,
          leasings: record.status.leasingStatus.expectations,
          color: '#ffd023',
          type: 'waiting',
          tooltipTitle: 'Ожидают ответа'
        });
        hasBankResponses ||= activeExpectationsBanks.length > 0;
      }

      if (
        record.status.approvedAlternatives.length > 0 ||
        record.status.leasingStatus.approvedAlternatives.length > 0
      ) {
        responses.push({
          banks: record.status.approvedAlternatives,
          leasings: record.status.leasingStatus.approvedAlternatives,
          color: '#2f78ee',
          type: 'alternative',
          tooltipTitle: 'Одобрена альтернатива'
        });
        hasBankResponses ||=
          record.status.approvedAlternatives.length > 0 ||
          record.status.leasingStatus.approvedAlternatives.length > 0;
      }

      if (
        record.status.denied.length > 0 ||
        record.status.leasingStatus.denied.length > 0
      ) {
        responses.push({
          banks: record.status.denied,
          leasings: record.status.leasingStatus.denied,
          color: '#e03137',
          type: 'decline',
          tooltipTitle: 'Отклонено'
        });
        hasBankResponses ||= record.status.denied.length > 0;
      }

      if (
        record.status.errors.length > 0 ||
        record.status.leasingStatus.errors.length > 0
      ) {
        responses.push({
          banks: record.status.errors,
          leasings: record.status.leasingStatus.errors,
          color: '#a8a8a8',
          type: 'error',
          tooltipTitle: 'Ошибка банка'
        });
        hasBankResponses ||= record.status.errors.length > 0;
      }

      const refundStatus = record.responses.find(
        (item) =>
          item.status === 'refunded' || item.status === 'refund_in_progress'
      );

      if (record.status.code === 'biometrics_failed') {
        return (
          <div className="auto-credit-table__big-status auto-credit-table__big-status--error">
            <p className="auto-credit-table__bank-text auto-credit-table__bank-text--center">
              {record.status.name}
            </p>
          </div>
        );
      }

      return refundStatus ? (
        <ChosenBankStatus
          img={bank?.icon || bankIconAndName?.icon}
          name={bank?.fullname || bankIconAndName?.name}
          status={{
            code: refundStatus.status,
            name: 'Возврат'
          }}
        />
      ) : record.status.isFinanced ? (
        <ChosenBankStatus
          img={bank?.icon || bankIconAndName?.icon}
          name={bank?.fullname || bankIconAndName?.name}
          status={{
            code: 'financed',
            name: 'Профинансировано'
          }}
        />
      ) : record.status.isSelected ? (
        <ChosenBankStatus
          img={bank?.icon || bankIconAndName?.icon}
          name={bank?.fullname || bankIconAndName?.name}
          status={{ code: 'chosen' }}
        />
      ) : record.status.clientAccepted ? (
        <ChosenBankStatus
          img={bank?.icon || bankIconAndName?.icon}
          name={bank?.fullname || bankIconAndName?.name}
          status={{ code: 'clientAccepted', name: 'Банк выбран клиентом' }}
        />
      ) : record.status.leasingStatus.isFinanced ? (
        <ChosenBankStatus
          img={
            record.status.leasingStatus.selectedLeasing?.leasingCompany.icon ||
            ''
          }
          name={
            record.status.leasingStatus.selectedLeasing?.leasingCompany.name ||
            ''
          }
          status={{ code: 'financed', name: 'Профинансировано' }}
          isLeasing
        />
      ) : record.status.leasingStatus.isSelected ? (
        <ChosenBankStatus
          img={
            record.status.leasingStatus.selectedLeasing?.leasingCompany.icon ||
            ''
          }
          name={
            record.status.leasingStatus.selectedLeasing?.leasingCompany.name ||
            ''
          }
          status={{ code: 'chosen', name: 'Лизинг выбран' }}
          isLeasing
        />
      ) : (
        <div className="auto-credit-table__statuses-wrapper">
          {responses.length > 0 ? (
            responses.map((item) => {
              const isWaiting = item.type === 'waiting';

              return (
                <StatusTooltip
                  key={item.color}
                  title={item.tooltipTitle}
                  textColor={item.color}
                  titleChildren={
                    <>
                      {isWaiting && <TableDate date={record.created_at.date} />}
                      {item.banks.map((res, index) => {
                        return (
                          <div
                            className="auto-credit-table__tooltip-bank"
                            key={`${res.status} ${index}`}
                          >
                            <RenderBankLogo selectedBank={res.bank} />
                            <div className="auto-credit-table__tooltip-bank-content">
                              <div className="auto-credit-table__tooltip-bank-header">
                                <span className="auto-credit-table__tooltip-bank-name">
                                  {banksDict?.getBy.name[res.bank]?.fullname ||
                                    ''}
                                </span>
                              </div>
                              <span className="auto-credit-table__tooltip-time">
                                от{' '}
                                {dayjs(res.responseDate || null).format(
                                  'DD.MM.YYYY, HH:mm'
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {item.leasings.map((res, index) => {
                        return (
                          <div
                            className="auto-credit-table__tooltip-bank"
                            key={`${res.status} ${index} leasing`}
                          >
                            <RenderBankLogo
                              src={res.leasingCompany.icon || ''}
                            />
                            <div className="auto-credit-table__tooltip-bank-content">
                              <div className="auto-credit-table__tooltip-bank-header">
                                <span className="auto-credit-table__tooltip-bank-name">
                                  {res.leasingCompany.name}
                                </span>
                                <span className="auto-credit-table__tooltip-leasing-badge">
                                  Лизинг
                                </span>
                              </div>
                              <span className="auto-credit-table__tooltip-time">
                                от{' '}
                                {dayjs(res.createdAt || null).format(
                                  'DD.MM.YYYY, HH:mm'
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  }
                >
                  <div
                    style={{ background: item.color }}
                    className="auto-credit-table__status"
                  >
                    <span
                      className={`auto-credit-table__bank-text ${isWaiting ? 'auto-credit-table__bank-text--dark' : ''}`}
                    >
                      {(item.banks.length ?? 0) + (item.leasings.length ?? 0) ||
                        ''}
                    </span>
                  </div>
                </StatusTooltip>
              );
            })
          ) : record.status.isAddedToQueue ? (
            <StatusTooltip title={'В процессе оформления'}>
              <div className="auto-credit-table__big-status">
                <p className="auto-credit-table__bank-text auto-credit-table__bank-text--center">
                  {record.status.name}
                </p>
              </div>
            </StatusTooltip>
          ) : record.status.isDraft ? (
            <StatusTooltip title={'В процессе оформления'}>
              <div className="auto-credit-table__big-status">
                <p className="auto-credit-table__bank-text auto-credit-table__bank-text--center">
                  {record.status.name}
                </p>
              </div>
            </StatusTooltip>
          ) : record.status.isUnverified ? (
            <StatusTooltip title={'В процессе валидации ОТП'}>
              <div className="auto-credit-table__big-status auto-credit-table__big-status--unverified">
                <p className="auto-credit-table__bank-text auto-credit-table__bank-text--center">
                  {record.status.name}
                </p>
              </div>
            </StatusTooltip>
          ) : (
            record.status.isError && (
              <div className="auto-credit-table__big-status auto-credit-table__big-status--error">
                <p className="auto-credit-table__bank-text auto-credit-table__bank-text--center">
                  Ошибка
                </p>
              </div>
            )
          )}{' '}
          {!hasBankResponses &&
            record.sentTo !== 'leasing' &&
            !record.status.isAddedToQueue &&
            !record.status.isDraft &&
            !record.status.isUnverified &&
            !record.status.isError && (
              <div
                style={{ background: '#FFD023' }}
                className="auto-credit-table__status"
              >
                <p className="auto-credit-table__bank-text auto-credit-table__bank-text--dark">
                  {banksDict?.content.filter((item) => item.is_active).length}
                </p>
              </div>
            )}
        </div>
      );
    }
  }
];

export default autoCreditTableColumns;
